import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UpdateDurationFilter } from '@app-challenged/actions/duration-filter.actions';
import { getDurationFilterState, State } from '@app-challenged/reducers';
import { ChallengedService } from '@app-challenged/services/challenged/challenged.service';
import { BREAKPOINTS_XSMALL, CHALLENGED_STATUS_LIST, speedSignTag, speedSignTagFilter } from '@app-core/constants/constants';
import { ChildConfigs, FleetEvents } from '@app-core/models/core.model';
import { DataService } from '@app-core/services/data/data.service';
import { DateService } from '@app-core/services/date/date.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-challenged',
  templateUrl: './challenged.component.html',
  styleUrls: ['./challenged.component.scss'],
})
export class ChallengedComponent implements OnInit, OnDestroy {
  public incidentTypeList = [];
  public challengedStatusList = CHALLENGED_STATUS_LIST;
  public currentMetricUnit: string;
  public loader = true;
  public startDate: string;
  public endDate: string;
  public fleetId: string;
  public selectedChallengedStatus = 'all';
  public selectedIncidentType = 'incidents';
  public incidentType = new FormControl('incidents');
  public challengeStatus = new FormControl('all');
  public challengeList = [];
  public subEventType = new FormControl('');
  public displaySubEventSelection: boolean;
  public subEventList = [];
  public fleetEvents: FleetEvents;
  public isTouchDevice = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public translate: TranslateService,
    public dataService: DataService,
    private gtmService: GoogleTagManagerService,
    private store: Store<State>,
    private challengedService: ChallengedService,
    private dateService: DateService,
    private breakpointObserver: BreakpointObserver,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit() {
    const combinedEventsList = this.dataService.modifyIncidentTypeList();

    this.incidentTypeList = [
      {
        label: 'All Events',
        value: 'incidents',
        type: 'INCIDENT',
      },
      ...combinedEventsList,
    ];

    this.breakpointObserver.observe(BREAKPOINTS_XSMALL).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilterState) => {
        ({ paramStartDate: this.startDate, paramEndDate: this.endDate } = durationFilterState);
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.getChallengedIncidents();
        }
      });

    this.dataService._currentMetricUnit.subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.getChallengedIncidents();
        }
      }
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        this.getChallengedIncidents();
      }
    });

    if (this.incidentType) {
      this.displaySubEventSelection = !!this.incidentType.value && this.getChildConfigs(this.incidentType.value).length > 0;
      this.incidentType.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((val) => {
        this.displaySubEventSelection = !!val && this.getChildConfigs(val).length > 0;
        if (this.displaySubEventSelection) {
          this.subEventList = [...this.getChildConfigs(this.incidentType.value).sort((a, b) => (a.label > b.label ? 1 : -1))];
          this.filterChildConfigs();

          if (this.incidentType.value === 'Traffic-Speed-Violated') {
            this.subEventList = [...speedSignTagFilter, ...this.subEventList].sort((a, b) => (a.label > b.label ? 1 : -1));
          }
          this.setDefaultSubEventType();
        } else {
          this.subEventList = [];
        }

        const bkType = this.incidentTypeList.filter((x) => x.value === val)[0].value || 'incidents';
        this.selectedIncidentType = bkType;
        const selectedType = this.incidentTypeList.filter((x) => x.value === val);
        this.gtmService.changeChallangeVideoTypeFilter(selectedType[0].label);
        this.mixpanelService.track('Change Challenge Video type filter', {
          Label: selectedType[0].label,
        });
        this.getChallengedIncidents();
      });
      this.subEventType.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.getChallengedIncidents());
    }

    if (this.challengeStatus) {
      this.challengeStatus.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((val) => {
        this.selectedChallengedStatus = this.challengedStatusList.filter((x) => x.value === val)[0].value;
        this.gtmService.changeChallengeStatusTypeFilter(this.selectedChallengedStatus);
        this.mixpanelService.track('Change Challenge Status filter', {
          Status: this.selectedChallengedStatus,
        });
        this.getChallengedIncidents();
      });
    }
  }

  public getChildConfigs(eventKey: string): ChildConfigs[] {
    const selectedEvent = this.dataService.modifyIncidentTypeList().find((event) => event.value === eventKey);
    if (selectedEvent && selectedEvent.childConfigs) {
      return Object.entries(selectedEvent.childConfigs).map(([key, value]: [string, { label: string }]) => ({ key, label: value.label }));
    }
    return [];
  }

  // Method to set the default value for subEventType
  public setDefaultSubEventType() {
    const childConfigs = this.getChildConfigs(this.incidentType.value);
    if (childConfigs.length > 0) {
      this.subEventType.setValue(childConfigs[0].key);
    }
  }

  public filterChildConfigs() {
    this.fleetEvents = this.dataService.getFleetEvents();
    if (this.fleetEvents && this.fleetEvents.standardEvents.length) {
      let fleetSubEvents = this.fleetEvents.standardEvents.filter((f) =>
        this.subEventList.some((subEvent) => subEvent.key === f.eventType)
      );
      this.subEventList = this.subEventList.filter((subEvent) => {
        const matchedSubEvvents = fleetSubEvents.find((event) => event.eventType === subEvent.key);
        // If matchedSubEvvents is undefined, it means subEvent.key is not in fleetEvents
        // If matchedSubEvvents is defined, check its state and include in subEventList accordingly
        return !matchedSubEvvents || matchedSubEvvents.state !== 'DISABLED';
      });
    }
  }

  public changeDuration(durationChange: any): void {
    const {
      paramStartDate: newStartDate = '',
      paramEndDate: newEndDate = '',
      displayStartDate = null,
      displayEndDate = null,
    } = durationChange || {};
    const oldStartDate = this.startDate;
    const oldEndDate = this.endDate;
    const isTimeRangeChanged = newStartDate !== oldStartDate || newEndDate !== oldEndDate;
    if (isTimeRangeChanged) {
      const noOfDays = this.dateService.getNoOfDays(newStartDate, newEndDate);
      const durationInText = this.dateService.getDurationText(displayStartDate, displayEndDate, noOfDays);
      this.gtmService.changeChallengeOverviewDurationTypeFilter(durationInText, noOfDays);
    }
    this.store.dispatch(new UpdateDurationFilter(durationChange));
    this.mixpanelService.track('Change Date Range in Challenges Page', {
      'Start Date': this.startDate,
      'End Date': this.endDate,
    });
  }

  public getChallengedIncidents(isRefresh?: boolean) {
    this.loader = true;
    let status = {};
    switch (this.selectedChallengedStatus) {
      case 'all':
        status = {};
        break;
      case 'pending':
        status = {
          challengeResolved: false,
        };
        break;
      case 'rejected':
        status = {
          challengeAccepted: false,
          challengeResolved: true,
        };
        break;
      case 'accepted':
        status = {
          challengeAccepted: true,
          challengeResolved: true,
        };
        break;
      default:
        status = {};
    }
    const params = {
      offset: 0,
      limit: 500,
      sortBy: 'timestamp',
      sort: 'desc',
      startDate: this.startDate,
      endDate: this.endDate,
      ...status,
    };

    const childConfigs = this.getChildConfigs(this.incidentType.value);
    const arrayOfSpeedSignTags = Object.keys(speedSignTag);

    if (childConfigs.length > 0 && this.selectedIncidentType !== 'incidents') {
      if (arrayOfSpeedSignTags.includes(this.subEventType.value)) {
        params['eventType'] = this.selectedIncidentType;
        params['speedSignTag'] = this.subEventType.value;
      } else if (this.subEventType.value !== childConfigs[0].key) {
        params['eventType'] = this.subEventType.value;
      } else {
        params['eventType'] = this.selectedIncidentType;
      }
    } else if (this.selectedIncidentType !== 'incidents') {
      params['eventType'] = this.selectedIncidentType;
    }

    this.challengedService
      .getChallengedIncidents(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res) => {
          this.challengeList = res.rows;
        },
        () => {
          this.challengeList = [];
        }
      );
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
